"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.experimentEvalFilterColsWithOptions = exports.experimentEvalFilterColumns = exports.observationEvalFilterColsWithOptions = exports.eventsEvalFilterColumns = exports.observationEvalFilterColumns = exports.mapEventEvalFilterColumnIdToField = exports.observationEvalVariableColumns = exports.experimentTargetEvalVariableColumns = exports.eventTargetEvalVariableColumns = exports.CODE_EVAL_TEMPLATE_VARIABLES = exports.toolCallForEvalSchema = exports.observationForEvalSchema = void 0;
exports.zipToolCallsFromRecord = zipToolCallsFromRecord;
exports.zipObservationToolCalls = zipObservationToolCalls;
exports.convertEventRecordToObservationForEval = convertEventRecordToObservationForEval;
exports.getCodeEvalVariableMapping = getCodeEvalVariableMapping;
const zod_1 = require("zod");
const types_1 = require("../../server/ingestion/types");
const observations_1 = require("../../domain/observations");
const metadata_conversion_1 = require("../../server/utils/metadata_conversion");
const json_1 = require("../../utils/json");
const flexibleUsageCostSchema = zod_1.z.record(zod_1.z.string(), zod_1.z.coerce.number().nullable());
exports.observationForEvalSchema = zod_1.z.object({
    // Identifiers
    span_id: zod_1.z.string(),
    trace_id: zod_1.z.string(),
    project_id: zod_1.z.string(),
    parent_span_id: zod_1.z.string().nullish(),
    // Core properties
    type: zod_1.z.string(),
    name: zod_1.z.string(),
    environment: zod_1.z.string().default(types_1.DEFAULT_TRACE_ENVIRONMENT),
    version: zod_1.z.string().nullish(),
    level: zod_1.z.string().default(observations_1.ObservationLevel.DEFAULT),
    status_message: zod_1.z.string().nullish(),
    // Trace-level properties
    trace_name: zod_1.z.string().nullish(),
    user_id: zod_1.z.string().nullish(),
    session_id: zod_1.z.string().nullish(),
    is_app_root: zod_1.z.boolean().default(false),
    tags: zod_1.z.array(zod_1.z.string()).default([]),
    release: zod_1.z.string().nullish(),
    // Model
    provided_model_name: zod_1.z.string().nullish(),
    model_parameters: zod_1.z.unknown().nullish(),
    // Prompt
    prompt_id: zod_1.z.string().nullish(),
    prompt_name: zod_1.z.string().nullish(),
    // Accepts string, number, or any other type from ingestion
    prompt_version: zod_1.z.union([zod_1.z.string().nullish(), zod_1.z.number().nullish()]),
    // Usage & Cost - accepts number values directly from ingestion
    provided_usage_details: flexibleUsageCostSchema,
    provided_cost_details: flexibleUsageCostSchema,
    usage_details: flexibleUsageCostSchema,
    cost_details: flexibleUsageCostSchema,
    // Tool calls
    tool_definitions: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()).default({}),
    tool_calls: zod_1.z.array(zod_1.z.unknown()).default([]),
    tool_call_names: zod_1.z.array(zod_1.z.string()).default([]),
    tool_call_count: zod_1.z.number().default(0),
    // Experiment
    experiment_id: zod_1.z.string().nullish(),
    experiment_name: zod_1.z.string().nullish(),
    experiment_description: zod_1.z.string().nullish(),
    experiment_dataset_id: zod_1.z.string().nullish(),
    experiment_item_id: zod_1.z.string().nullish(),
    experiment_item_expected_output: zod_1.z.string().nullish(),
    experiment_item_metadata: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()).nullish(),
    experiment_item_root_span_id: zod_1.z.string().nullish(),
    // Data - accepts any type (string, array, object) from different OTEL SDKs
    input: zod_1.z.unknown().nullish(),
    output: zod_1.z.unknown().nullish(),
    metadata: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()).nullish(),
});
/**
 * Self-contained tool call shape handed to evaluators. Rebuilt from the
 * ClickHouse storage layout, which keeps names in a parallel array
 * (`tool_call_names`) so ClickHouse can filter without JSON parsing.
 */
exports.toolCallForEvalSchema = zod_1.z.object({
    id: zod_1.z.string(),
    name: zod_1.z.string(),
    arguments: zod_1.z.unknown(),
    type: zod_1.z.string(),
    index: zod_1.z.number(),
});
/**
 * zipObservationToolCalls for camelCase records with loosely typed arrays
 * (tRPC/domain shapes: events batchIO, legacy observations.byId). Malformed
 * names map to "" instead of being dropped — filtering would shift the zip
 * and misattribute every later call's name.
 */
function zipToolCallsFromRecord(record) {
    const { toolCalls, toolCallNames } = record;
    return zipObservationToolCalls({
        tool_calls: Array.isArray(toolCalls) ? toolCalls : [],
        tool_call_names: Array.isArray(toolCallNames)
            ? toolCallNames.map((name) => (typeof name === "string" ? name : ""))
            : [],
    });
}
/**
 * Zips the parallel arrays back into named tool call objects.
 * `tool_call_names` is authoritative for count and order: ingestion writes
 * both arrays in lockstep (`convertCallsToArrays`), and stored entries carry
 * no name. `arguments` arrives double-encoded (a JSON string inside the entry
 * JSON) and is parsed to an object; unparsable values stay raw strings.
 */
function zipObservationToolCalls(observation) {
    return observation.tool_call_names.map((name, i) => {
        const parsed = (0, json_1.parseJsonIfString)(observation.tool_calls[i]);
        const entry = typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)
            ? parsed
            : {};
        return {
            id: typeof entry.id === "string" ? entry.id : "",
            name,
            arguments: (0, json_1.parseJsonIfString)(entry.arguments) ?? {},
            type: typeof entry.type === "string" ? entry.type : "",
            index: typeof entry.index === "number" ? entry.index : 0,
        };
    });
}
/**
 * Zips parallel name/value arrays into a plain object, first occurrence wins.
 * Collects into a `Map` so that keys shared with `Object.prototype` (`toString`,
 * `constructor`, `__proto__`, ...) are treated as ordinary metadata keys.
 */
function zipFirstOccurrenceWins(names, values) {
    const entries = new Map();
    names.forEach((name, i) => {
        if (!entries.has(name)) {
            entries.set(name, values[i]);
        }
    });
    return Object.fromEntries(entries);
}
function convertEventRecordToObservationForEval(record) {
    const metadata = (0, metadata_conversion_1.metadataArraysToRecord)(record.metadata_names, record.metadata_values);
    const experimentItemMetadata = record.experiment_item_metadata_names.length > 0
        ? zipFirstOccurrenceWins(record.experiment_item_metadata_names, record.experiment_item_metadata_values)
        : undefined;
    const toolCallNames = record.tool_call_names ?? [];
    return exports.observationForEvalSchema.parse({
        ...record,
        metadata,
        experiment_item_metadata: experimentItemMetadata,
        tool_call_count: toolCallNames.length,
    });
}
/**
 * Canonical variable set for code evaluators — one entry per experiment
 * target column below (the id annotation on the column arrays pins them to
 * this list). Web synthesizes rule mappings from it
 * (getCodeEvalVariableMapping) and buildCodeEvalPayload (codeEvalExecution)
 * places each variable in the evaluator payload — see the
 * CODE_EVAL_PAYLOAD_SECTION_BY_VARIABLE tripwire in codeEvalDispatcherTypes.
 */
exports.CODE_EVAL_TEMPLATE_VARIABLES = [
    "input",
    "output",
    "metadata",
    "toolCalls",
    "experimentItemExpectedOutput",
    "experimentItemMetadata",
];
function getCodeEvalVariableMapping() {
    return exports.CODE_EVAL_TEMPLATE_VARIABLES.map((variable) => ({
        templateVariable: variable,
        selectedColumnId: variable,
        jsonSelector: null,
    }));
}
exports.eventTargetEvalVariableColumns = [
    {
        id: "input",
        name: "Input",
        description: "Observation input data",
        internal: "input",
    },
    {
        id: "output",
        name: "Output",
        description: "Observation output data",
        internal: "output",
    },
    {
        id: "metadata",
        name: "Metadata",
        description: "Observation metadata",
        type: "stringObject",
        internal: "metadata",
    },
    {
        id: "toolCalls",
        name: "Tool Calls",
        description: "Tool calls recorded on the observation ({id, name, arguments, type, index})",
        internal: "tool_calls",
    },
];
exports.experimentTargetEvalVariableColumns = [
    ...exports.eventTargetEvalVariableColumns,
    {
        id: "experimentItemExpectedOutput",
        name: "Expected Output",
        description: "Expected output from experiment item",
        internal: "experiment_item_expected_output",
    },
    {
        id: "experimentItemMetadata",
        name: "Experiment Item Metadata",
        description: "Metadata from experiment item",
        type: "stringObject",
        internal: "experiment_item_metadata",
    },
];
/**
 * Columns available for variable extraction in observation-based evals.
 * These are the fields that can be mapped to template variables.
 *
 * When configuring an eval, users can map these columns to template
 * variables like {{input}}, {{output}}, {{expected_output}}, etc.
 */
exports.observationEvalVariableColumns = [
    ...exports.experimentTargetEvalVariableColumns,
];
var filterFields_1 = require("./filterFields");
Object.defineProperty(exports, "mapEventEvalFilterColumnIdToField", { enumerable: true, get: function () { return filterFields_1.mapEventEvalFilterColumnIdToField; } });
var spanFilters_1 = require("./spanFilters");
Object.defineProperty(exports, "observationEvalFilterColumns", { enumerable: true, get: function () { return spanFilters_1.observationEvalFilterColumns; } });
Object.defineProperty(exports, "eventsEvalFilterColumns", { enumerable: true, get: function () { return spanFilters_1.eventsEvalFilterColumns; } });
Object.defineProperty(exports, "observationEvalFilterColsWithOptions", { enumerable: true, get: function () { return spanFilters_1.observationEvalFilterColsWithOptions; } });
var experimentFilters_1 = require("./experimentFilters");
Object.defineProperty(exports, "experimentEvalFilterColumns", { enumerable: true, get: function () { return experimentFilters_1.experimentEvalFilterColumns; } });
Object.defineProperty(exports, "experimentEvalFilterColsWithOptions", { enumerable: true, get: function () { return experimentFilters_1.experimentEvalFilterColsWithOptions; } });
